import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

const NIVEL = {
  ATENCAO: { l: "Atenção", c: "bg-aj-amber-soft text-aj-orange-hover" },
  ALTO_RISCO: { l: "Alto Risco", c: "bg-orange-100 text-orange-800" },
  CRITICO: { l: "Crítico", c: "bg-red-100 text-[#B91C1C]" },
  NORMAL: { l: "Normal", c: "bg-[#DCFCE7] text-[#166534]" },
};

export default function AlertasOperacionais() {
  const [list, setList] = useState(null);
  const [filtro, setFiltro] = useState("abertos");
  const [solucoes, setSolucoes] = useState({});

  const load = useCallback(() => {
    const p = filtro === "abertos" ? "?resolvido=false" : filtro === "resolvidos" ? "?resolvido=true" : "";
    api.get(`/alertas-operacionais${p}`).then(r => setList(r.data)).catch(e => toast.error(formatApiError(e)));
  }, [filtro]);
  useEffect(load, [load]);

  const resolver = async (id) => {
    const sol = (solucoes[id] || "").trim();
    if (!sol) { toast.error("Descreva a solução adotada"); return; }
    try {
      await api.put(`/alertas-operacionais/${id}/resolver`, { solucao: sol });
      toast.success("Alerta resolvido");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="alertas-operacionais-page">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">Prevenção</div>
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-aj-blue-deep mt-1">Alertas Operacionais</h1>
        <p className="text-sm text-aj-text-muted mt-1 max-w-2xl">
          Risco preventivo em 4 níveis (tempo real × previsão). O sistema <b>nunca cancela ou redistribui sozinho</b> — você acompanha, avisa o cliente, reagenda ou registra a solução.
        </p>
      </div>

      <div className="flex gap-2">
        {["abertos", "resolvidos", "todos"].map(f => (
          <button key={f} onClick={() => setFiltro(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider ${filtro === f ? "bg-aj-blue-deep text-white" : "bg-white border border-border text-aj-text-muted"}`}
            data-testid={`alertas-filtro-${f}`}>{f}</button>
        ))}
      </div>

      <div className="space-y-3">
        {list === null && <div className="aj-card p-10 text-center text-aj-text-muted">Carregando…</div>}
        {list?.length === 0 && (
          <div className="aj-card p-10 text-center text-aj-text-muted" data-testid="alertas-vazio">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-40" /> Nenhum alerta {filtro !== "todos" ? filtro.slice(0, -1) : ""}. Operação saudável!
          </div>
        )}
        {list?.map(a => {
          const nv = NIVEL[a.nivel] || NIVEL.NORMAL;
          return (
            <div key={a.id} className="aj-card p-5 border-l-4 border-l-[#FF6700]" data-testid={`alerta-op-${a.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <AlertTriangle size={15} className="text-aj-orange" />
                    <span className="font-mono text-xs">{a.pim_code}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${nv.c}`}>{nv.l}</span>
                    {a.resolvido && <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-[#DCFCE7] text-[#166534]">Resolvido</span>}
                  </div>
                  <div className="text-sm mt-1"><b>{a.produto}</b> · {a.cliente_cidade || "—"}</div>
                  <div className="text-xs text-aj-text-muted mt-0.5">
                    {a.detalhes?.decorrido_min}min decorridos de ~{a.detalhes?.estimado_min}min previstos
                    {a.detalhes?.proximos_clientes_hoje ? ` · ${a.detalhes.proximos_clientes_hoje} próximo(s) cliente(s) hoje` : ""}
                    {" · "}{a.criado_em ? new Date(a.criado_em).toLocaleString("pt-BR") : ""}
                  </div>
                  {a.solucao && <div className="text-xs mt-1 text-[#166534]">Solução: {a.solucao}</div>}
                </div>
                {!a.resolvido && (
                  <div className="flex gap-2 w-full sm:w-auto">
                    <Input placeholder="Solução adotada…" value={solucoes[a.id] || ""}
                      onChange={e => setSolucoes(s => ({ ...s, [a.id]: e.target.value }))}
                      className="h-9 text-sm w-full sm:w-56" data-testid={`alerta-solucao-${a.id}`} />
                    <Button size="sm" onClick={() => resolver(a.id)} className="btn-sharp brand-bg text-white h-9"
                      data-testid={`alerta-resolver-${a.id}`}>Resolver</Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
